import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { GridCellParams } from '@mui/x-data-grid-pro';

import type { TableColumnType, TableRowType, TableType } from '../../../../grid/reduxStore/table.types';
import { ColumnTypeIdentifier, RowTypesIdentifier, TotalRowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { SidePanelProviderContext } from '../../SidePanelModelsProvider';
import { isRowBelongsToTotal } from '../../../../../../muiTheme/dataGridUtils';

import { EditorDefaultSidePanelType, setActiveSidePanel } from '../../../../grid/reduxStore/blockStyleSettingsSlice';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import type { columns } from '../../../../GridDndEditor/Block/Table';

import {
  selectContentTableRows,
  selectContentTableColumns,
  selectTableTotalRows,
  selectTotalSelectedRow,
} from '../../../../grid/reduxStore/editorSlice';
import { useTableManipulation } from '../../../../GridDndEditor/Block/Table/useTableManipulation';
import { useTableContentUndoRedo } from '../../../../hooks/useTableContentUndoRedo.ts';
import { defaultRowMeta } from '../../../../GridDndEditor/Block/Table/variables';
import { useBlockStyleSettings } from '../../../../hooks/useBlockSettings';
import {
  optionalColumn,
  setOfRowsForPricingTable,
  setOfRowsForPricingTableWithReferences,
} from '../../../../GridDndEditor/Block/Table/defaultData';
import { TableDefaultClassNamesEnum } from '../../../../grid/reduxStore/textDefaultStylingSlice.ts';
import { getSanitizedDataForTableFooter } from 'utils/getSanitizedDataForTableFooter';
import useFeatureFlag from '../../../../../../hooks/useFeatureFlag.ts';
import { FeatureFlags } from '../../../../../../utils/featureFlags.ts';
import { useNewBlockDeletedHandler } from '../../../../hooks/useNewBlockDeletedHandler.ts';
import { getIsCellEditingAllowed } from '../../../../GridDndEditor/Block/Table/tableUtils/customCellHelpers.tsx';
import { useTableTotals } from '../../../../GridDndEditor/Block/Table/useTableTotals';
import { isGeneratedRow } from '../../../../grid/reduxStore/crmRowMarkers';

export enum TableRowAddPositions {
  BEFORE = 0,
  AFTER = +1,
}

const EMPTY_COLUMNS: TableColumnType[] = [];
const EMPTY_ROWS: TableRowType[] = [];
const EMPTY_IDS: string[] = [];

export const useTableRowManipulation = () => {
  const dispatch = useAppDispatch();
  const [, setIsModalVisible] = useState(false);
  const { deleteBlock } = useBlockStyleSettings();
  const deleteHandler = useNewBlockDeletedHandler();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const getTableTotals = useTableTotals();
  const { toggledTableSettingsPanel, setToggledTableSettingsPanel } = useContext(SidePanelProviderContext);
  const { getTableData, addCustomFieldToRow } = useTableManipulation();
  const { selectedBlockIdByWrapper, selectedSectionId: sectionId } = useContext(SelectionContext);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const isUpdatingRef = useRef(false);
  const isCurrencySettingsEnabled = useFeatureFlag([FeatureFlags.currencySettings])[FeatureFlags.currencySettings];
  const rowsForPricingTable = isCurrencySettingsEnabled ? setOfRowsForPricingTableWithReferences : setOfRowsForPricingTable;

  const allTextTableRows = useSelector((state: RootState) =>
    selectContentTableRows(state, sectionId || '', selectedBlockIdByWrapper as string)
  );
  const allTextTableColumns = useSelector((state: RootState) => {
    if (!sectionId) return EMPTY_COLUMNS;
    return selectContentTableColumns(state, sectionId, selectedBlockIdByWrapper as string) ?? EMPTY_COLUMNS;
  });
  const totalRows = useSelector((state: RootState) => {
    if (!sectionId) return EMPTY_ROWS;
    return selectTableTotalRows(state, sectionId, selectedBlockIdByWrapper as string) ?? EMPTY_ROWS;
  });

  const totalSelectedRow = useSelector((state: RootState) =>
    sectionId ? selectTotalSelectedRow(state, sectionId, selectedBlockIdByWrapper as string) : undefined
  );
  const isTotalSelectedRowIdActive = !!totalSelectedRow?.['id'];
  const rowsData = isTotalSelectedRowIdActive ? totalRows : allTextTableRows;
  const allRowIds = useMemo(() => rowsData?.map((r) => r.id) ?? EMPTY_IDS, [rowsData]);

  const withUpdateLock = useCallback(async (fn: () => Promise<void>) => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;
    setIsUpdating(true);
    try {
      await fn();
    } finally {
      isUpdatingRef.current = false;
      setIsUpdating(false);
    }
  }, []);

  const updatedTableState = async (blockId: string, updateRows: TableRowType[], updatedColumns: typeof columns) => {
    if (!sectionId) throw new Error('Section id cannot be empty');
    const tableData = getTableData(blockId, sectionId) as TableType;
    const optionalRowExists = updateRows?.some((row) => row?.isOptionalToggleAvailable === true);
    const columnsUpdatedData = optionalRowExists
      ? updatedColumns
      : updatedColumns.filter((col) => col.columnType !== ColumnTypeIdentifier.OPTIONAL);
    const { rows, totalRows, metadata } = getTableTotals(updateRows, columnsUpdatedData, tableData);
    const newTableData: TableType = {
      ...tableData,
      columns: columnsUpdatedData,
      rows,
    };

    handleTableContentChanged(blockId, sectionId, tableData, {
      ...newTableData,
      totalRows,
      metadata,
    });
  };

  const updateTableNonFooterRows = async (blockId: string, totalRows: TableRowType[], selectedRow: TableRowType) => {
    if (!sectionId) throw new Error('Section id cannot be empty');
    const tableData = getTableData(blockId, sectionId) as TableType;
    const updatedTableData: TableType = {
      ...tableData,
      totalRows: totalRows,
      selectedRow,
    };
    const { rows, metadata } = getTableTotals(updatedTableData.rows, updatedTableData.columns, updatedTableData);
    const newTableData: TableType = {
      ...updatedTableData,
      rows,
      metadata: metadata,
      selectedRow,
    };

    handleTableContentChanged(blockId, sectionId, tableData, newTableData);
  };

  const addNewEmptyRowAtIndexPosition = (
    rows: TableRowType[],
    positionIndex: number,
    duplicatedData: TableRowType,
    isTotalFooter = false
  ) => {
    let rowData: TableRowType;
    if (isTotalFooter) {
      const defaultRow = rowsForPricingTable.find((row) => row.rowType === TotalRowTypesIdentifier.DISCOUNT);
      if (!defaultRow) {
        throw new Error('Default row not found');
      }
      rowData = { ...defaultRow, id: uuidv4() };
    } else {
      rowData = { ...duplicatedData, ...defaultRowMeta, rowType: RowTypesIdentifier.BODY };
    }
    return addRowAtIndexPosition(rows, rowData, positionIndex);
  };

  const addRowAtIndexPosition = async (rows: TableRowType[], rowData: TableRowType, positionIndex: number) => {
    const newRowId = uuidv4();
    const newRowData = { ...rowData, id: newRowId };
    const newRows = [...rows];
    newRows.splice(positionIndex, 0, newRowData);
    if (!allTextTableColumns) return;
    if (isTotalSelectedRowIdActive) {
      await updateTableNonFooterRows(selectedBlockIdByWrapper as string, newRows, newRowData);
    } else {
      await updatedTableState(selectedBlockIdByWrapper as string, newRows, allTextTableColumns);
    }

    return newRowId;
  };

  const setCellFocus = (rowId: string) => {
    if (!toggledTableSettingsPanel || !selectedBlockIdByWrapper) return;

    if (!sectionId) throw new Error('Section id cannot be empty');

    const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;

    if (!isTotalSelectedRowIdActive) {
      const allColumns = tableData.columns;
      const firstEditableColumn = allColumns.find(
        (column) => column.editable && column.field && column.columnType !== ColumnTypeIdentifier.OPTIONAL
      );
      if (firstEditableColumn) {
        const tableCallbackDetails = toggledTableSettingsPanel.tableApi?.tableCallbackDetails;
        if (!tableCallbackDetails?.api?.getCellParams) return;

        setToggledTableSettingsPanel({
          tableApi: {
            selectedModel: tableCallbackDetails.api.getCellParams(rowId, firstEditableColumn.field),
            tableCallbackDetails: tableCallbackDetails,
          },
        });

        tableCallbackDetails.api.setCellFocus(rowId, firstEditableColumn.field);
      } else {
        setToggledTableSettingsPanel(null);
      }
    }
  };

  const getSelectedRowId = (): string | undefined => {
    let selectedRowId: string | undefined = undefined;
    if (toggledTableSettingsPanel) {
      const cellSelectedModel = toggledTableSettingsPanel.tableApi.selectedModel as GridCellParams;
      selectedRowId = cellSelectedModel.row.id;
    } else if (isTotalSelectedRowIdActive) {
      selectedRowId = totalSelectedRow?.['id'];
    }
    return selectedRowId;
  };

  const handleRowAdd = async (position: TableRowAddPositions) => {
    await withUpdateLock(async () => {
      const selectedRowId = getSelectedRowId();
      if (!selectedBlockIdByWrapper || !sectionId || !selectedRowId) return;

      const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;
      if (!tableData) return;

      const isTotalRow = isTotalSelectedRowIdActive;
      const currentRows = isTotalRow ? tableData.totalRows ?? [] : tableData.rows ?? [];
      const currentColumns = isTotalRow ? tableData.totalRows ?? [] : tableData.columns ?? [];

      if (!currentRows.length || !currentColumns.length) return;

      const selectedRowIndex = currentRows.findIndex((row) => row.id === selectedRowId);
      if (selectedRowIndex === -1) return;

      const sourceRow = currentRows[selectedRowIndex];

      // Can't insert relative to a generated/locked row.
      if (isGeneratedRow(sourceRow)) return;

      const duplicatedRowData: TableRowType = {} as TableRowType;
      const nonCellRowKeys = ['discount', 'manualSubtotalOverride', 'cellConfig'];
      Object.keys(sourceRow).forEach((key) => {
        if (nonCellRowKeys.includes(key)) return;
        if (!['id', 'rowType', '__reorder__', 'isOptionalToggleAvailable'].includes(key) && key !== RowTypesIdentifier.BODY) {
          duplicatedRowData[key] = `<p data-table-style='${TableDefaultClassNamesEnum.TABLE_BODY}'></p>`;
        }
      });

      const newRowId: string | undefined = await addNewEmptyRowAtIndexPosition(
        currentRows,
        selectedRowIndex + position,
        duplicatedRowData,
        isRowBelongsToTotal(sourceRow)
      );

      if (typeof newRowId === 'string') {
        setCellFocus(newRowId);
      }
    });
  };

  const handleRowDuplication = async () => {
    await withUpdateLock(async () => {
      const selectedRowId = getSelectedRowId();
      if (!selectedBlockIdByWrapper || !selectedRowId) return;

      const selectedRowIndex = rowsData.map((row) => row.id).indexOf(selectedRowId);
      const rowDataDuplicated = rowsData[selectedRowIndex];
      if (!rowDataDuplicated) return;

      // Generated/locked rows can't be duplicated directly — only their pattern row can.
      if (isGeneratedRow(rowDataDuplicated)) return;

      const newRowId = await addRowAtIndexPosition(rowsData, rowDataDuplicated, selectedRowIndex + TableRowAddPositions.AFTER);

      if (newRowId) {
        setCellFocus(newRowId);
      }
    });
  };

  const handleDeleteRow = async () => {
    await withUpdateLock(async () => {
      const selectedRowId = getSelectedRowId();
      if (!selectedBlockIdByWrapper || !toggledTableSettingsPanel || !selectedRowId) return;
      if (!sectionId) throw new Error('Section id cannot be empty');
      const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;

      const selectedRow = tableData.rows.find((row) => row.id === selectedRowId);
      if (isGeneratedRow(selectedRow)) return;

      // Deleting an authored/pattern row cascades to its generated children — otherwise they're
      // left orphaned with a crmSourceRowId pointing at a row that no longer exists.
      const updatedRows = tableData.rows.filter((row) => row.id !== selectedRowId && row.crmSourceRowId !== selectedRowId);

      if (!updatedRows.length) {
        if (!sectionId) return;

        deleteBlock(selectedBlockIdByWrapper);
        deleteHandler(sectionId, selectedBlockIdByWrapper).then(() => {
          setIsModalVisible(false);
          dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
        });
        return;
      }

      const idIndex = allRowIds.indexOf(selectedRowId);
      const newSelectedIndex = idIndex - 1 >= 0 ? idIndex - 1 : idIndex + 1;
      const newSelectedRowId = allRowIds[newSelectedIndex];
      await updatedTableState(selectedBlockIdByWrapper, [...updatedRows], [...tableData.columns]);

      const tableCallbackDetails = toggledTableSettingsPanel.tableApi.tableCallbackDetails;
      tableCallbackDetails.api.setRowSelectionModel([newSelectedRowId]);

      setCellFocus(newSelectedRowId);
    });
  };

  const toggleRowProperty = async (property: string, status?: boolean, id?: string) => {
    const selectedRowId = id || getSelectedRowId();
    if (!selectedRowId || !selectedBlockIdByWrapper) return;
    if (!sectionId) throw new Error('Required parameters are missing or invalid.');

    if (isGeneratedRow(allTextTableRows?.find((row) => row.id === selectedRowId))) return;

    const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;
    const updatedRows = allTextTableRows?.map((row) =>
      row?.id === selectedRowId ? { ...row, [property]: status ?? !row[property] } : row
    );

    if (property === 'isOptionalToggleAvailable' && status !== undefined) {
      const tableUpdatedContent = await toggleRowOptionalStatus(status, updatedRows);
      if (tableUpdatedContent) {
        await updatedTableState(selectedBlockIdByWrapper, tableUpdatedContent.rows, tableUpdatedContent.columns);
      }
    } else {
      await updatedTableState(selectedBlockIdByWrapper, updatedRows, tableData.columns);
    }
  };

  const toggleRowPropertyById = async (
    rowId: string | undefined,
    property: keyof TableRowType,
    value?: TableRowType[keyof TableRowType]
  ) => {
    if (!rowId || !selectedBlockIdByWrapper || !sectionId) return;
    if (isGeneratedRow(allTextTableRows?.find((row) => row.id === rowId))) return;

    const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;
    const updatedRows = allTextTableRows?.map((row) => {
      if (row?.id !== rowId) return row;
      const newRowType = property === 'rowType' ? (value as string) : row.rowType;
      const sanitized = property === 'rowType' ? getSanitizedDataForTableFooter(row, tableData.columns, undefined, newRowType) : row;
      return { ...sanitized, [property]: value ?? !row[property] };
    });

    if (!updatedRows) return;
    await updatedTableState(selectedBlockIdByWrapper, updatedRows, tableData.columns);
  };

  async function toggleRowOptionalStatus(status: boolean, rows: any) {
    if (!selectedBlockIdByWrapper || !sectionId) return;

    try {
      const tableContentData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;
      const { columns } = tableContentData;

      const COLUMN_INDEX_ACTIVE = 1;
      const COLUMN_INDEX_INACTIVE = 2;
      const columnIndexToCopy = status ? COLUMN_INDEX_ACTIVE : COLUMN_INDEX_INACTIVE;
      const slicedColumns = columns.slice(columnIndexToCopy);
      const optionalRows = rows.filter((row) => row.isOptionalToggleAvailable && row.rowType === RowTypesIdentifier.BODY);
      const isOnlyOneOptionalRow = optionalRows.length === 1;
      const optionalRowNotExists = optionalRows.length <= 0;
      let updatedColumns;

      if (!status) {
        updatedColumns = !optionalRowNotExists ? columns : [columns[0], ...slicedColumns];
      } else {
        updatedColumns = isOnlyOneOptionalRow ? [columns[0], optionalColumn, ...slicedColumns] : columns;
      }

      return {
        ...tableContentData,
        columns: updatedColumns,
        rows: rows,
      };
    } catch (error) {
      throw new Error('Error while updating table data');
    }
  }

  async function handleDeleteTotalRow() {
    await withUpdateLock(async () => {
      if (!selectedBlockIdByWrapper || !sectionId) return;
      const totalSelectedRowId = totalSelectedRow?.['id'];
      if (totalRows) {
        const indexBeforeSelected = totalRows.findIndex((row) => row.id === totalSelectedRowId) - 1;
        const updatedRows = totalRows.filter((row) => row.id !== totalSelectedRowId);
        await updateTableNonFooterRows(selectedBlockIdByWrapper, updatedRows, updatedRows[indexBeforeSelected]);
      }
    });
  }

  const toggleCellEditingById = useCallback(
    async (rowId: string | undefined, columnField: string | undefined) => {
      if (!rowId || !columnField || !selectedBlockIdByWrapper || !sectionId) return;

      const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;
      const currentRow = tableData.rows?.find((row) => row.id === rowId);
      const currentColumn = tableData.columns?.find((col) => col.field === columnField);
      if (!currentRow || !currentColumn) return;
      if (isGeneratedRow(currentRow)) return;

      const currentIsEditingActive = getIsCellEditingAllowed({ row: currentRow, colDef: currentColumn });
      const cellConfigObject = { isEditingActive: !currentIsEditingActive };

      const updatedTableData = addCustomFieldToRow({
        blockId: selectedBlockIdByWrapper,
        sectionId,
        field: columnField,
        id: rowId,
        cellConfigObject,
      });

      if (!updatedTableData) return;
      const { rows, totalRows, metadata } = getTableTotals(updatedTableData.rows, updatedTableData.columns, tableData);
      const nextTableData: TableType = {
        ...updatedTableData,
        rows,
        totalRows,
        metadata,
      };

      handleTableContentChanged(selectedBlockIdByWrapper, sectionId, tableData, nextTableData);
    },
    [selectedBlockIdByWrapper, sectionId, getTableData, addCustomFieldToRow, handleTableContentChanged]
  );

  const handleInsertTotalRowByType = useCallback(
    async (rowType: TotalRowTypesIdentifier) => {
      await withUpdateLock(async () => {
        if (!selectedBlockIdByWrapper || !sectionId) return;

        const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;
        if (!tableData) return;

        const currentTotalRows = tableData.totalRows ?? [];

        const defaultRow = rowsForPricingTable.find((row) => row.rowType === rowType);
        if (!defaultRow) throw new Error(`Default row not found for type: ${rowType}`);

        const lastOfTypeIndex = currentTotalRows.reduce((lastIdx, row, idx) => (row.rowType === rowType ? idx : lastIdx), -1);

        let insertIndex: number;
        if (lastOfTypeIndex >= 0) {
          insertIndex = lastOfTypeIndex + 1;
        } else {
          const totalRowIndex = currentTotalRows.findIndex((row) => row.rowType === TotalRowTypesIdentifier.TOTAL);
          insertIndex = totalRowIndex >= 0 ? totalRowIndex : currentTotalRows.length;
        }

        const newRowData = { ...defaultRow, id: uuidv4() };
        const newRows = [...currentTotalRows];
        newRows.splice(insertIndex, 0, newRowData);

        await updateTableNonFooterRows(selectedBlockIdByWrapper, newRows, newRowData);
      });
    },
    [selectedBlockIdByWrapper, sectionId, getTableData, rowsForPricingTable, updateTableNonFooterRows, withUpdateLock]
  );

  const handleHideSubtotalRow = useCallback(async () => {
    await withUpdateLock(async () => {
      if (!selectedBlockIdByWrapper || !sectionId) return;

      const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;
      const currentSelectedRow = tableData.selectedRow;
      if (!currentSelectedRow) return;

      const newHiddenState = !currentSelectedRow.isRowHidden;
      const updateRows = (rows: TableRowType[]) =>
        rows.map((row) => (row.id === currentSelectedRow.id ? { ...row, isRowHidden: newHiddenState } : row));

      const updatedTable: TableType = {
        ...tableData,
        rows: tableData.rows ? updateRows(tableData.rows) : tableData.rows,
        totalRows: tableData.totalRows ? updateRows(tableData.totalRows) : tableData.totalRows,
        selectedRow: { ...currentSelectedRow, isRowHidden: newHiddenState },
      };

      handleTableContentChanged(selectedBlockIdByWrapper, sectionId, tableData, updatedTable);
    });
  }, [selectedBlockIdByWrapper, sectionId, getTableData, handleTableContentChanged, withUpdateLock]);

  return {
    handleRowAdd,
    handleRowDuplication,
    handleDeleteRow,
    toggleRowProperty,
    handleDeleteTotalRow,
    updateTableNonFooterRows,
    isUpdating,
    toggleRowPropertyById,
    toggleCellEditingById,
    handleInsertTotalRowByType,
    handleHideSubtotalRow,
  };
};
